import React, { useState, useMemo } from 'react';
import { useApp } from '../context/AppContext';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths, startOfQuarter, endOfQuarter, subQuarters, startOfYear, isWithinInterval } from 'date-fns';
import {
  DollarSign, Calendar, Users, Download,
  TrendingUp
} from 'lucide-react';
import { formatCurrency } from '../utils/invoiceData';
import './TipsReport.css';

// Safely convert any date value (ISO string, Firestore Timestamp, Date, epoch) to a Date object
const toDate = (val) => {
  if (!val) return null;
  if (val instanceof Date) return val;
  if (typeof val === 'string') {
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof val.toDate === 'function') return val.toDate(); // Firestore Timestamp
  if (typeof val === 'number') return new Date(val);
  return null;
};

const safeFormat = (date, fmt) => {
  try {
    return format(date, fmt);
  } catch {
    return '—';
  }
};

function TipsReport() {
  const { state } = useApp();
  const [dateRange, setDateRange] = useState('this_month');
  const [selectedEmployee, setSelectedEmployee] = useState('all');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [sortField, setSortField] = useState('paidDate');
  const [sortDir, setSortDir] = useState('desc');

  // Memoize date bounds to prevent infinite re-renders
  const dateBounds = useMemo(() => {
    const today = new Date();
    switch (dateRange) {
      case 'this_week':
        return {
          start: startOfWeek(today, { weekStartsOn: 1 }),
          end: endOfWeek(today, { weekStartsOn: 1 })
        };
      case 'last_week':
        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);
        return {
          start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          end: endOfWeek(lastWeek, { weekStartsOn: 1 })
        };
      case 'this_month':
        return {
          start: startOfMonth(today),
          end: endOfMonth(today)
        };
      case 'last_month':
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        return {
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth)
        };
      case 'this_quarter':
        return {
          start: startOfQuarter(today),
          end: endOfQuarter(today)
        };
      case 'last_quarter':
        const lq = subQuarters(today, 1);
        return {
          start: startOfQuarter(lq),
          end: endOfQuarter(lq)
        };
      case 'last_3_months':
        return {
          start: startOfMonth(subMonths(today, 2)),
          end: endOfMonth(today)
        };
      case 'year_to_date':
        return {
          start: startOfYear(today),
          end: today
        };
      case 'custom':
        return {
          start: customStartDate ? new Date(customStartDate + 'T00:00:00') : today,
          end: customEndDate ? new Date(customEndDate + 'T23:59:59') : today
        };
      default:
        return { start: today, end: today };
    }
  }, [dateRange, customStartDate, customEndDate]);

  // Get all invoices with tips in the date range
  const tipsData = useMemo(() => {
    const invoicesWithTips = state.invoices.filter(inv => {
      if (!inv.tipAmount || inv.tipAmount <= 0) return false;
      if (!inv.paidAt) return false;

      const paidDate = toDate(inv.paidAt);
      if (!paidDate) return false;
      return isWithinInterval(paidDate, { start: dateBounds.start, end: dateBounds.end });
    });

    // Build tip entries
    const tipEntries = [];
    invoicesWithTips.forEach(inv => {
      if (inv.tipSplit && inv.tipSplit.length > 0) {
        inv.tipSplit.forEach(split => {
          tipEntries.push({
            invoiceId: inv.id,
            invoiceNumber: inv.invoiceNumber,
            clientName: inv.clientName,
            jobDate: inv.jobDate,
            paidDate: inv.paidAt,
            employeeId: split.employeeId,
            employeeName: split.employeeName,
            tipAmount: split.amount,
            totalTip: inv.tipAmount,
            teamSize: inv.tipSplit.length
          });
        });
      } else {
        // Fallback: split tip among job's team when tipSplit is missing
        const job = inv.jobId ? state.jobs.find(j => j.id === inv.jobId) : null;
        const teamMembers = job ? [...new Set([...(job.team || []), ...(job.helpers || [])])] : [];
        if (teamMembers.length > 0) {
          const splitAmount = Math.round((inv.tipAmount / teamMembers.length) * 100) / 100;
          teamMembers.forEach(empId => {
            const emp = state.employees.find(e => e.id === empId);
            tipEntries.push({
              invoiceId: inv.id,
              invoiceNumber: inv.invoiceNumber,
              clientName: inv.clientName,
              jobDate: inv.jobDate,
              paidDate: inv.paidAt,
              employeeId: empId,
              employeeName: emp ? `${emp.firstName} ${emp.lastName || ''}`.trim() : 'Unknown',
              tipAmount: splitAmount,
              totalTip: inv.tipAmount,
              teamSize: teamMembers.length
            });
          });
        } else {
          // No team info — show as unassigned
          tipEntries.push({
            invoiceId: inv.id,
            invoiceNumber: inv.invoiceNumber,
            clientName: inv.clientName,
            jobDate: inv.jobDate,
            paidDate: inv.paidAt,
            employeeId: 'unassigned',
            employeeName: 'Unassigned',
            tipAmount: inv.tipAmount,
            totalTip: inv.tipAmount,
            teamSize: 1
          });
        }
      }
    });

    return tipEntries;
  }, [state.invoices, state.jobs, state.employees, dateBounds]);

  // Filter by employee if selected
  const filteredTips = useMemo(() => {
    let tips = tipsData;
    if (selectedEmployee !== 'all') {
      tips = tips.filter(tip => tip.employeeId === selectedEmployee);
    }
    // Sort
    return [...tips].sort((a, b) => {
      let valA, valB;
      switch (sortField) {
        case 'paidDate':
          valA = a.paidDate || '';
          valB = b.paidDate || '';
          break;
        case 'clientName':
          valA = a.clientName || '';
          valB = b.clientName || '';
          break;
        case 'employeeName':
          valA = a.employeeName || '';
          valB = b.employeeName || '';
          break;
        case 'tipAmount':
          valA = a.tipAmount;
          valB = b.tipAmount;
          break;
        case 'totalTip':
          valA = a.totalTip;
          valB = b.totalTip;
          break;
        default:
          valA = a.paidDate || '';
          valB = b.paidDate || '';
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [tipsData, selectedEmployee, sortField, sortDir]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Aggregate by employee
  const employeeTotals = useMemo(() => {
    const totals = {};
    tipsData.forEach(tip => {
      if (!totals[tip.employeeId]) {
        totals[tip.employeeId] = {
          employeeId: tip.employeeId,
          employeeName: tip.employeeName,
          totalTips: 0,
          tipCount: 0
        };
      }
      totals[tip.employeeId].totalTips += tip.tipAmount;
      totals[tip.employeeId].tipCount += 1;
    });
    return Object.values(totals)
      .map(emp => ({ ...emp, avgTip: emp.tipCount > 0 ? emp.totalTips / emp.tipCount : 0 }))
      .sort((a, b) => b.totalTips - a.totalTips);
  }, [tipsData]);

  // Grand total and averages
  const grandTotal = useMemo(() => {
    return filteredTips.reduce((sum, tip) => sum + tip.tipAmount, 0);
  }, [filteredTips]);

  const avgTipPerEntry = useMemo(() => {
    return filteredTips.length > 0 ? grandTotal / filteredTips.length : 0;
  }, [grandTotal, filteredTips]);

  // Count unique invoices
  const uniqueInvoiceCount = useMemo(() => {
    return new Set(filteredTips.map(t => t.invoiceId)).size;
  }, [filteredTips]);

  // Get unique employees for filter
  const employees = useMemo(() => {
    const empMap = {};
    state.employees.forEach(emp => {
      empMap[emp.id] = `${emp.firstName} ${emp.lastName || ''}`.trim();
    });
    return empMap;
  }, [state.employees]);

  // Export to CSV
  const exportToCSV = () => {
    const lines = [];

    // Report header
    lines.push(['Tips Report']);
    lines.push([`Date Range: ${safeFormat(dateBounds.start, 'MM/dd/yyyy')} - ${safeFormat(dateBounds.end, 'MM/dd/yyyy')}`]);
    if (selectedEmployee !== 'all') {
      const empName = employees[selectedEmployee] || selectedEmployee;
      lines.push([`Employee: ${empName}`]);
    }
    lines.push([]);

    // Summary
    lines.push(['SUMMARY']);
    lines.push(['Total Tips', grandTotal.toFixed(2)]);
    lines.push(['Tip Entries', filteredTips.length]);
    lines.push(['Invoices with Tips', uniqueInvoiceCount]);
    lines.push(['Avg Tip per Entry', avgTipPerEntry.toFixed(2)]);
    lines.push([]);

    // Employee breakdown
    const relevantTotals = selectedEmployee === 'all'
      ? employeeTotals
      : employeeTotals.filter(e => e.employeeId === selectedEmployee);
    lines.push(['TIPS BY EMPLOYEE']);
    lines.push(['Employee', 'Tips Earned', 'Tip Count', 'Avg per Tip']);
    relevantTotals.forEach(emp => {
      lines.push([emp.employeeName, emp.totalTips.toFixed(2), emp.tipCount, emp.avgTip.toFixed(2)]);
    });
    lines.push([]);

    // Detail rows
    lines.push(['TIP DETAILS']);
    lines.push(['Date Paid', 'Invoice #', 'Client', 'Employee', 'Split Amount', 'Team Size', 'Total Tip']);
    filteredTips.forEach(tip => {
      lines.push([
        safeFormat(toDate(tip.paidDate), 'MM/dd/yyyy'),
        tip.invoiceNumber,
        `"${tip.clientName}"`,
        `"${tip.employeeName}"`,
        tip.tipAmount.toFixed(2),
        tip.teamSize,
        tip.totalTip.toFixed(2)
      ]);
    });

    // Grand total row
    lines.push([]);
    lines.push(['', '', '', 'TOTAL', grandTotal.toFixed(2)]);

    const csv = lines.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tips-report-${safeFormat(dateBounds.start, 'yyyy-MM-dd')}-to-${safeFormat(dateBounds.end, 'yyyy-MM-dd')}.csv`;
    a.click();
  };

  if (state.loading && !state.initialized) {
    return (
      <div className="tips-report" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div className="spinner" />
          <p>Loading tips report...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tips-report">
      {/* Header */}
      <div className="report-header">
        <div className="header-title">
          <h1>Tips Report</h1>
          <p className="subtitle">Track and export tip payouts for your team</p>
        </div>
        <button className="btn-primary" onClick={exportToCSV}>
          <Download size={18} />
          Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Date Range</label>
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
          >
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_quarter">This Quarter</option>
            <option value="last_quarter">Last Quarter</option>
            <option value="last_3_months">Last 3 Months</option>
            <option value="year_to_date">Year to Date</option>
            <option value="custom">Custom Range</option>
          </select>
        </div>

        {dateRange === 'custom' && (
          <>
            <div className="filter-group">
              <label>Start Date</label>
              <input
                type="date"
                value={customStartDate}
                onChange={(e) => setCustomStartDate(e.target.value)}
              />
            </div>
            <div className="filter-group">
              <label>End Date</label>
              <input
                type="date"
                value={customEndDate}
                onChange={(e) => setCustomEndDate(e.target.value)}
              />
            </div>
          </>
        )}

        <div className="filter-group">
          <label>Employee</label>
          <select 
            value={selectedEmployee} 
            onChange={(e) => setSelectedEmployee(e.target.value)}
          >
            <option value="all">All Employees</option>
            {Object.entries(employees).map(([id, name]) => (
              <option key={id} value={id}>{name}</option>
            ))}
          </select>
        </div>

        <div className="date-range-display">
          <Calendar size={16} />
          <span>
            {safeFormat(dateBounds.start, 'MMM d, yyyy')} - {safeFormat(dateBounds.end, 'MMM d, yyyy')}
          </span>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        <div className="summary-card total">
          <div className="card-icon">
            <DollarSign size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">{formatCurrency(grandTotal)}</span>
            <span className="card-label">Total Tips</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">
            <TrendingUp size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">{formatCurrency(avgTipPerEntry)}</span>
            <span className="card-label">Avg per Entry</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">
            <Calendar size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">{uniqueInvoiceCount}</span>
            <span className="card-label">Invoices with Tips</span>
          </div>
        </div>

        <div className="summary-card">
          <div className="card-icon">
            <Users size={24} />
          </div>
          <div className="card-content">
            <span className="card-value">{employeeTotals.length}</span>
            <span className="card-label">Employees</span>
          </div>
        </div>
      </div>

      {/* Employee Summary */}
      {employeeTotals.length > 0 && (
        <div className="employee-summary">
          <h2>Tips by Employee</h2>
          <div className="employee-grid">
            {employeeTotals.map(emp => (
              <div key={emp.employeeId} className="employee-card">
                <div className="emp-avatar">
                  {emp.employeeName.charAt(0)}
                </div>
                <div className="emp-info">
                  <span className="emp-name">{emp.employeeName}</span>
                  <span className="emp-count">{emp.tipCount} tips &middot; avg {formatCurrency(emp.avgTip)}</span>
                </div>
                <div className="emp-total">
                  {formatCurrency(emp.totalTips)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Detailed Table */}
      <div className="tips-table-section">
        <h2>Tip Details</h2>
        {filteredTips.length === 0 ? (
          <div className="empty-state">
            <DollarSign size={48} />
            <p>No tips found for the selected period</p>
          </div>
        ) : (
          <table className="tips-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('paidDate')}>
                  Date Paid {sortField === 'paidDate' && <span className="sort-arrow">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                </th>
                <th>Invoice</th>
                <th className="sortable" onClick={() => handleSort('clientName')}>
                  Client {sortField === 'clientName' && <span className="sort-arrow">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                </th>
                <th className="sortable" onClick={() => handleSort('employeeName')}>
                  Employee {sortField === 'employeeName' && <span className="sort-arrow">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                </th>
                <th className="right sortable" onClick={() => handleSort('tipAmount')}>
                  Split {sortField === 'tipAmount' && <span className="sort-arrow">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                </th>
                <th className="right sortable" onClick={() => handleSort('totalTip')}>
                  Total Tip {sortField === 'totalTip' && <span className="sort-arrow">{sortDir === 'asc' ? '\u25B2' : '\u25BC'}</span>}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTips.map((tip, idx) => (
                <tr key={`${tip.invoiceId}-${tip.employeeId}-${idx}`}>
                  <td>{safeFormat(toDate(tip.paidDate), 'MMM d, yyyy')}</td>
                  <td className="invoice-num">{tip.invoiceNumber}</td>
                  <td>{tip.clientName}</td>
                  <td>
                    <div className="employee-cell">
                      <span className="emp-badge">{tip.employeeName.charAt(0)}</span>
                      {tip.employeeName}
                    </div>
                  </td>
                  <td className="right amount">{formatCurrency(tip.tipAmount)}</td>
                  <td className="right muted">
                    {formatCurrency(tip.totalTip)}
                    {tip.teamSize > 1 && <span className="team-note"> ÷ {tip.teamSize}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan="4"><strong>Total</strong></td>
                <td className="right"><strong>{formatCurrency(grandTotal)}</strong></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
}

export default TipsReport;
